import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import useAuth from '@/modules/auth/hooks/useAuth.hook';
import Button from '../design-system/Button';
import StatusScreen from '../design-system/StatusScreen';
import { isPublicPageRoute } from '../utils/is-public-page-route';

export function RootErrorBoundary() {
  const { state: authState } = useAuth();

  const error = useRouteError() as Error;
  const eventId = Sentry.captureException(error);

  const showCrashDialog = () => {
    Sentry.showReportDialog({
      eventId,
      user: authState.user?._id ? { name: authState.user.name, email: authState.user.email } : {},
      subtitle: 'Please tell us what happened below',
      subtitle2: '',
      labelComments: 'A detailed report would help us fix the issue faster',
    });
  };

  showCrashDialog();

  // A visitor who crashed on someone's public page has no business in the app —
  // send them to the marketing site instead of the editor.
  const isPublicPage = isPublicPageRoute();
  const escapeUrl = isPublicPage ? process.env.HOME_URL! : `${process.env.CLIENT_URL!}/playground`;

  // The raw message goes to Sentry and the report dialog, not to the user: it is
  // written for us ("Failed to fetch dynamically imported module…"), and on a
  // public page it would be shown to someone who does not even have an account.
  const status = isRouteErrorResponse(error) ? `${error.status} ${error.statusText}`.trim() : null;

  return (
    <StatusScreen
      fullScreen
      tone="danger"
      icon={<ExclamationTriangleIcon />}
      eyebrow={status}
      title="Something went wrong"
      description="This page hit an unexpected error. Our team has been notified — reloading usually gets you moving again."
      actions={
        <>
          <Button
            type="button"
            className="w-full whitespace-nowrap px-6 py-2.5 sm:w-auto"
            onClick={() => window.location.reload()}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Reload the page
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            className="w-full whitespace-nowrap px-6 py-2.5 sm:w-auto"
            onClick={() => (window.location.href = escapeUrl)}
          >
            {isPublicPage ? 'Go to Linkie' : 'Back to my Linkie'}
          </Button>
        </>
      }
    />
  );
}
